import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { GestureHandling } from "leaflet-gesture-handling"
import { FrameRenderer } from "@hotwired/turbo"
import { LocateControl } from "leaflet.locatecontrol";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";
import 'leaflet.markercluster'
import _ from "lodash"
import { mapboxToken } from "../helpers/mapbox_token"

export default class extends Controller {
  static targets = ["container", "listItem"]
  static values = {
    loggables: Array,
    baseUrl: String,
    markerPaths: Object
  }

  initialize() {
    this.map = this.initMap();
    this.markers = new Map();
    this.mapSearchOverlay = document.getElementById('map-search-overlay');
    this.searchNewAreaBtn = document.getElementById('search-new-area-btn');
    this.lastSearchCenter = null;
    this.lastSearchZoom = null;
  }

  connect() {
    console.log('SearchMapController connected');
    this.searchNewAreaBtn.addEventListener('click', this.searchNewArea.bind(this));

    const storedLocation = localStorage.getItem('checkin_location');
    if (storedLocation) {
      console.log('Found stored location:', storedLocation);
      const loc = JSON.parse(storedLocation);
      this.map.setView([loc.lat, loc.lng], loc.zoom);
    } else {
      console.log('No stored location found, attempting geolocation or default location');
      this.map.locate({
        watch: true,
        setView: true,
        maxZoom: 10,
        enableHighAccuracy: true
      });
    }
  }

  handleLoggablesChanged(event) {
    console.log('Loggables changed event received:', event.detail.loggables);
    this.loggablesValue = event.detail.loggables;
  }

  loggablesValueChanged() {
    console.log('Loggables value changed:', this.loggablesValue);
    this.setMarkers();
  }

  setMarkers() {
    console.log('Setting markers for loggables:', this.loggablesValue);
    if (!this.map) return;
    // Remove all markers
    if (this.markers.size > 0) {
      console.log('Removing existing markers from map...');
      this.markers.forEach(marker => this.map.removeLayer(marker));
      this.markers.clear();
    }

    console.log("markerPathsValue:", this.markerPathsValue);
    var baseOptions = { popupAnchor: [12, 0], shadowUrl: this.markerPathsValue.shadow };
    var ArtIcon = L.Icon.extend({ options: { popupAnchor: [12, 0], shadowUrl: this.markerPathsValue.shadow } });
    // custom marker icon
    var PlaceIcon = L.Icon.extend({ options: { ...baseOptions, iconUrl: this.markerPathsValue.place } });
    var ActivityIcon = L.Icon.extend({ options: { ...baseOptions, iconUrl: this.markerPathsValue.activity } });
    var CommunityEventIcon = L.Icon.extend({ options: { ...baseOptions, iconUrl: this.markerPathsValue.communityEvent } });
    var GeographyIcon = L.Icon.extend({ options: { ...baseOptions, iconUrl: this.markerPathsValue.geography } });
    var CustomPassportItemIcon = L.Icon.extend({ options: { ...baseOptions, iconUrl: this.markerPathsValue.customPassportItem } });

    var icons = {
      place: new PlaceIcon(),
      activity: new ActivityIcon(),
      community_event: new CommunityEventIcon(),
      geography: new GeographyIcon(),
      custom_passport_item: new CustomPassportItemIcon(),
    };

    // Add new markers
    var markerGroup = L.markerClusterGroup({chunkedLoading: true});
    this.loggablesValue.forEach(loggable => {
      if (loggable.latitude && loggable.longitude) {
        const marker = L.marker([loggable.latitude, loggable.longitude], { icon: icons[loggable.type] })
          .bindPopup(loggable.name)
        this.markers.set(loggable.id, marker);
        marker.on('click', () => {
          this.highlightListItem && this.highlightListItem(loggable.id);
        });
      }
    });
    markerGroup.addLayers([...this.markers.values()]);
    this.map.addLayer(markerGroup);
  }

  handleLocationInput(e) {
    if (e.key === 'Enter') {
      const address = this.locationInput.value.trim();
      if (address.length === 0) return;
      this.geocodeAddress(address);
    }
  }

  geocodeAddress(address) {
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxToken()}`)
      .then(res => res.json())
      .then(data => {
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          this.map.setView([lat, lng], 11);
        } else {
          alert('Location not found.');
        }
      });
  }

  handleMapMove() {
    console.log('Map move detected, checking if search should be triggered...');
    if (!this.map) return;
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();

    // Only show search button if moved significantly (e.g., >0.05 degrees lat/lng or zoom changed)
    if (this.lastSearchCenter && this.lastSearchZoom !== null) {
      if (
        this.distanceBetween(center, this.lastSearchCenter) < 0.05 &&
        zoom === this.lastSearchZoom
      ) {
        console.log('Map moved, but not enough to trigger search button.');
        this.mapSearchOverlay.classList.add('d-none');
        return;
      }
      else {
        console.log('Map moved significantly, showing search button...');
        this.mapSearchOverlay.classList.remove('d-none');
      }
    }
    else {
      console.log('No previous search center or zoom, searching new area...');
      this.searchNewArea();
    }
  }

  searchNewArea() {
    if (!this.map) return;

    console.log('Searching new area...');
    const bounds = this.map.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const params = new URLSearchParams({
      ne_lat: ne.lat,
      ne_lng: ne.lng,
      sw_lat: sw.lat,
      sw_lng: sw.lng
    });

    fetch(`${this.baseUrlValue}?${params.toString()}`, {
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    })
      .then(response => response.text())
      .then(html => {
        this.mapSearchOverlay.classList.add('d-none');
        this.lastSearchCenter = this.map.getCenter();
        this.lastSearchZoom = this.map.getZoom();
        localStorage.setItem('checkin_location', JSON.stringify({ lat: this.lastSearchCenter.lat, lng: this.lastSearchCenter.lng, address: 'Search Area', zoom: this.lastSearchZoom }));
        Turbo.renderStreamMessage(html);
      });
  }

  // Helper to compute distance in degrees
  distanceBetween(a, b) {
    const dLat = Math.abs(a.lat - b.lat);
    const dLng = Math.abs(a.lng - b.lng);
    return Math.sqrt(dLat * dLat + dLng * dLng);
  }

  initMap(center = [0, 0]) {
    console.log('Initializing map with center:', center);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    //L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);
    const map = L.map(this.containerTarget, {
      zoomControl: false,
      center: center,
      zoom: 15,
      maxZoom: 19,
      minZoom: 7
    });

    const outdoors = L.tileLayer(`https://api.mapbox.com/styles/v1/allroadstaken/clzbxv6l3002l01p66v9eb66s/tiles/256/{z}/{x}/{y}@2x?access_token=${mapboxToken()}`, {
      maxZoom: 19,
      minZoom: 1,
      attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });

    map.addLayer(outdoors);
    map.on('moveend', _.debounce(this.handleMapMove.bind(this), 500));

    const locateControl = new LocateControl({
      position: 'bottomright',
      icon: 'fa-solid fa-location-crosshairs',
      iconLoading: 'fa-solid fa-spinner fa-spin',
      setView: 'untilPanOrZoom',
      locateOptions: {
        maxZoom: 10
      }
    });
    locateControl.addTo(map);

    L.control.zoom( { position: 'bottomright' }).addTo(map);

    return map;
  }

  highlightListItem(loggableId) {
    this.listItemTargets.forEach(item => {
      item.classList.remove('highlight');
    });
    const selectedItem = this.listItemTargets.find(item => 
      item.dataset.loggableId === loggableId.toString()
    );
    if (selectedItem) {
      selectedItem.classList.add('highlight');
      selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  focusMap(event) {
    const loggableId = event.currentTarget.dataset.loggableId;
    const marker = this.markers.get(parseInt(loggableId));
    if (marker) {
      this.map.setView(marker.getLatLng(), 13);
      marker.openPopup();
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
} 
